/**
 * Initial Data Hydration wrapper for RTK Query baseQuery
 *
 * This baseQuery wrapper can short-circuit query requests by returning initial data from
 * Redux (`state.initialState.value`) once, keyed by a hydration-cache entry.
 *
 * Usage:
 * 1. Opt-in per endpoint via RTK Query `extraOptions`, e.g. `extraOptions: { initialDataKey: 'group' }`
 *    (or provide `initialDataKeyResolver` / `initialDataResolver`).
 * 2. Ensure `state.initialState.value` contains the data for the resolved key.
 * 3. Optionally provide `validateInitialDataParams` to validate request parameters before
 *    returning initial data. If validation fails, falls back to the underlying baseQuery.
 *
 * Behavior:
 * - Applies to queries only (mutations always call the underlying baseQuery)
 * - Resolves `initialDataKey` via `initialDataKeyResolver(args)` or falls back to `initialDataKey`
 * - Validates request parameters via `validateInitialDataParams` if provided (optional)
 * - Computes a hydration cache key via `hydrationCacheKeyResolver` or defaults to
 *   `initialDataKey:${initialDataKey}` (or just `endpoint` when no key is resolved).
 *   This ensures an initial-data key is consumed only once globally unless a custom cache key
 *   is provided.
 * - If the cache key was already hydrated, falls back to the underlying baseQuery
 * - Otherwise returns `{ data }` from `initialDataResolver(...)` or `initialState[initialDataKey]`,
 *   and marks the cache key as hydrated
 */

import {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
  FetchBaseQueryMeta,
} from '@reduxjs/toolkit/query'
import { type RootState } from '@wellesley/store'
import { remoteOpenapiHydrationCache } from '../../hydrationCache/remoteOpenapiHydrationCache'

export type BQuery = BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError,
  object,
  FetchBaseQueryMeta
>

interface InitialDataOptions {
  initialDataKey?: string
  initialDataKeyResolver?: (params: {
    endpoint: string
    requestArgs: string | FetchArgs
  }) => string | undefined
  initialDataResolver?: (params: {
    endpoint: string
    requestArgs: string | FetchArgs
    initialState: unknown
  }) => unknown
  hydrationCacheKeyResolver?: (params: {
    endpoint: string
    requestArgs: string | FetchArgs
    initialDataKey: string | undefined
  }) => string
  validateInitialDataParams?: (params: {
    endpoint: string
    requestArgs: string | FetchArgs
  }) => boolean
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null

const hydrationCache = remoteOpenapiHydrationCache()

export const baseQueryWithInitialData =
  (
    baseQuery: BQuery,
  ): BaseQueryFn<
    string | FetchArgs,
    unknown,
    FetchBaseQueryError,
    InitialDataOptions,
    FetchBaseQueryMeta
  > =>
  async (args, api, extraOptions) => {
    if (api.type === 'mutation') {
      return baseQuery(args, api, extraOptions)
    }

    const endpoint = api.endpoint

    if (!endpoint) {
      return baseQuery(args, api, extraOptions)
    }

    const hasInitialDataConfig = Boolean(
      extraOptions?.initialDataKey ||
        extraOptions?.initialDataKeyResolver ||
        extraOptions?.initialDataResolver,
    )

    if (!hasInitialDataConfig) {
      return baseQuery(args, api, extraOptions)
    }

    const resolvedInitialDataKey =
      extraOptions?.initialDataKeyResolver?.({
        endpoint,
        requestArgs: args,
      }) ?? extraOptions?.initialDataKey

    if (
      extraOptions?.validateInitialDataParams &&
      !extraOptions.validateInitialDataParams({
        endpoint,
        requestArgs: args,
      })
    ) {
      return baseQuery(args, api, extraOptions)
    }

    const hydrationCacheKey =
      extraOptions?.hydrationCacheKeyResolver?.({
        endpoint,
        requestArgs: args,
        initialDataKey: resolvedInitialDataKey,
      }) ??
      (resolvedInitialDataKey
        ? `initialDataKey:${resolvedInitialDataKey}`
        : endpoint)

    const isHydrated = await hydrationCache.has(hydrationCacheKey)

    if (isHydrated) {
      return baseQuery(args, api, extraOptions)
    }

    const state = api.getState() as RootState
    const initialState = state.initialState?.value as unknown
    const initialData =
      extraOptions?.initialDataResolver?.({
        endpoint,
        requestArgs: args,
        initialState,
      }) ??
      (() => {
        if (!resolvedInitialDataKey) return undefined

        return isRecord(initialState)
          ? initialState[resolvedInitialDataKey]
          : undefined
      })()

    if (initialData === undefined) {
      return baseQuery(args, api, extraOptions)
    }

    await hydrationCache.add(hydrationCacheKey)

    return { data: initialData }
  }
